import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Top-level error boundary — catches render & lifecycle errors so the app
 * never freezes on a white screen. Shows a friendly screen with a reset button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Persist in the ring buffer for dev inspection
    try {
      window.__VIDOC_ERRORS = window.__VIDOC_ERRORS || [];
      window.__VIDOC_ERRORS.push({
        kind: "react",
        time: new Date().toISOString(),
        message: error?.message,
        stack: info?.componentStack,
      });
    } catch (e) {
      // ring buffer write itself failed (e.g. quota) — non-blocking
      void e;
    }
    // eslint-disable-next-line no-console
    console.error("[VIDOC ErrorBoundary]", error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.href = "/";
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center bg-[var(--vidoc-bg-secondary)] px-4"
      >
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[var(--vidoc-border)] p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-50 mb-4">
            <AlertTriangle className="w-7 h-7 text-[var(--vidoc-warning)]" />
          </div>
          <h1 className="text-xl font-bold mb-2">Une erreur est survenue</h1>
          <p className="text-sm text-[var(--vidoc-text-secondary)] mb-1">
            VIDOC a rencontré un problème inattendu.
          </p>
          <p className="text-xs text-[var(--vidoc-text-tertiary)] mb-6 break-words">
            {String(error?.message || error).slice(0, 200)}
          </p>
          <div className="flex flex-col gap-2">
            <button
              data-testid="error-retry-btn"
              onClick={this.handleReset}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target hover:bg-[var(--vidoc-primary-dark)]"
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </button>
            <button
              data-testid="error-home-btn"
              onClick={this.handleReload}
              className="w-full py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] font-medium touch-target"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }
}
